import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { MdInventory } from "react-icons/md";
import { selectCurrentUser } from "../../../../store/slices/currentUserSlice";
import {
  CategoryInterface,
  GroupedProduct,
  PromoInterface,
} from "../../../../common/types/types";
import CategoryList from "../categoryList/CategoryList";
import CategoryTileList from "../../../category/components/categoryTileList/CategoryTileList";
import { useList } from "./useList";
import styles from "./List.module.scss";
import { ROUTES } from "../../../../common/utils/constants";
import { Banner } from "../banner/Banner";
import { EmptyState } from "../../../../common/components/emptyState/EmptyState";
import { selectHasUseStock } from "../../../../store/slices/saleItemListSlice";
import ItemList from "../itemList/ItemList";
import { useCommonLoading } from "../../../../common/hooks/useCommonLoading";
import { LeadForm } from "../../../../common/components/leadForm/LeadForm";
import { getLeadFeature } from "../../../../config/runtimeConfig";


type Props = {
  categoryList: CategoryInterface[] | [];
  promoList: PromoInterface[] | [];
  selectedCategory?: number;
  setSelectedCategory: (id: number | undefined) => void;
  getCategoryName: (id: number) => string | undefined;
  variant?: "products" | "categories" | "hybrid";
};

export default function List({
  categoryList,
  promoList,
  selectedCategory,
  setSelectedCategory,
  getCategoryName,
  variant = "products",
}: Props) {
  const navigate = useNavigate();
  const currentUser = useSelector(selectCurrentUser);
  const usingStock = useSelector(selectHasUseStock);
  const { commonLoading } = useCommonLoading();

  const {
    filteredList,
    handlePromoPress,
    searchQuery,
    effectiveCategory,
    handleSearchChange,
    handleSearchClear,
    handleCategorySelect,
    loadingMore,
    hasMore,
    loadMore,
  } = useList(selectedCategory, setSelectedCategory);

  const showCategories = variant === "products";
const leadFeature = getLeadFeature();
const showLeadForm = leadFeature?.enabled && !searchQuery;

  const handleGroupPress = (group: GroupedProduct) => {
    navigate(`${ROUTES.GROUP}/${group.groupId}`);
  };

  const getEmptyStateConfig = () => {
    if (searchQuery) {
      return {
        title: "Ничего не найдено",
        description: "Попробуйте изменить поисковый запрос",
      };
    }
    if (effectiveCategory) {
      return {
        title: "Нет товаров",
        description: "В этой категории пока нет товаров",
      };
    }
    return {
      title: "Список пуст",
      description: "Товары появятся после добавления",
    };
  };

  if (commonLoading && filteredList.length === 0) {
    return <div className={styles.container} />;
  }

  // CATEGORIES
  if (variant === "categories") {
    return (
      <div className={styles.container}>
        {promoList.length > 0 && (
          <div className={styles.promoContainer}>
            <div className={styles.space} />
            <Banner items={promoList} onItemPress={handlePromoPress} />
          </div>
        )}

        <CategoryTileList />
      </div>
    );
  }

  // HYBRID
  if (variant === "hybrid") {
    return (
      <div className={styles.container}>
        <CategoryList
          categoryList={categoryList}
          selectedCategory={effectiveCategory}
          onSelectCategory={handleCategorySelect}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onSearchClear={handleSearchClear}
          showCategories={false}
        />
        <div className={styles.listContainer}>
          {promoList.length > 0 && !searchQuery && (
            <div className={styles.promoContainer}>
              <Banner items={promoList} onItemPress={handlePromoPress} />
            </div>
          )}
          {showLeadForm && <LeadForm />}

          {!searchQuery && <CategoryTileList />}
          {filteredList.length === 0 ? (
            <EmptyState
              icon={MdInventory}
              title={getEmptyStateConfig().title}
              description={getEmptyStateConfig().description}
            />
          ) : (
            <div className={styles.list}>
              <ItemList
                items={filteredList}
                getCategoryName={getCategoryName}
                isAuth={Boolean(currentUser?.id)}
                usingStock={usingStock}
                onGroupPress={handleGroupPress}
                hasMore={hasMore}
                loadingMore={loadingMore}
                loadMore={loadMore}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // PRODUCTS (default)
  return (
    <div className={styles.container}>
      <CategoryList
        categoryList={categoryList}
        selectedCategory={effectiveCategory}
        onSelectCategory={handleCategorySelect}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onSearchClear={handleSearchClear}
        showCategories={showCategories}
      />

      <div className={styles.listContainer}>
        {promoList.length > 0 && !searchQuery && (
          <div className={styles.promoContainer}>
            <Banner items={promoList} onItemPress={handlePromoPress} />
          </div>
        )}

        {showLeadForm && <LeadForm />}

        {filteredList.length === 0 ? (
          <EmptyState
            icon={MdInventory}
            title={getEmptyStateConfig().title}
            description={getEmptyStateConfig().description}
          />
        ) : (
          <div className={styles.list}>
            <ItemList
              items={filteredList}
              getCategoryName={getCategoryName}
              isAuth={Boolean(currentUser?.id)}
              usingStock={usingStock}
              onGroupPress={handleGroupPress}
              hasMore={hasMore}
              loadingMore={loadingMore}
              loadMore={loadMore}
            />
          </div>
        )}
      </div>
    </div>
  );
}
